/**
 * Which free-talk skill credits get a chip on the learner's bubble (W6,
 * decisions D7 and D9). Pure: the whole thread in, at most one chip per bubble
 * out. Progress is SILENT by default — the credits land on every bubble
 * (`VoiceMessage.skillCredits`) and almost none is shown.
 *
 * The rules, in order:
 *
 *   1. Live bubbles only (not the history seeded by "Continuar" or a reload).
 *   2. Only after an `ok` corrections verdict: before it, the chip would appear
 *      and then vanish when a correction lands late; with `failed` / `skipped`
 *      (a newer turn superseded it, a game turn, …) the turn was never
 *      analysed, so nothing says the words were right. The server bounds the
 *      wait (2026-10-08): when the verdict comes more than 6 s after the
 *      credits, it sends them `silent` (rule 4), and a bubble whose credits
 *      are all silent is decided at once — it never holds a later bubble.
 *   3. The verdict wins over praise (`skillPraiseBlocked`, shared with the
 *      debrief): a credit is dropped when any error of the SAME turn names its
 *      skill or touches the words that earned it. Every error counts, even one
 *      the learner's sensitivity level or corrections toggle hides.
 *   4. What the server allows (`display`, owner 2026-10-07): `silent` (the
 *      lexicon's vocabulary — progress only, the debrief lists it) shows
 *      nothing, not even a completion; `nail-only` (the learner's radar focus
 *      judged although out of the chip scope) shows only "lo clavaste".
 *   5. One chip per bubble, by priority:
 *        a. `completed` — this credit completed the skill (`completedNow`) and
 *           the catalog celebrates it (`celebrate`), any area. It also fires
 *           the small sparkles (`completionCount`), no sound.
 *        b. `nailed` — "lo clavaste" (D9): the skill is the own skill of an
 *           active radar focus (`nailedFocus`), any area. Once per focus per
 *           session, by the server's record of the session's nails
 *           (`selectSkillChips`), and never while the thread holds an error of
 *           that skill, in any bubble (`nailRevokedBySession`): the server
 *           withholds the nail when the error came first; when it comes later,
 *           the bubble's chip falls back here and the debrief agrees.
 *        c. `grammar` — a counted rep (`counted`) of a `grammar` skill the
 *           catalog celebrates and that was not complete before this credit:
 *           "✓ voz pasiva · 1/3". Never set phrases or vocabulary. ONCE PER
 *           SKILL PER SESSION (owner, 2026-10-07): only while no earlier
 *           credit of the skill in this session showed a chip, by the server's
 *           record of the skill's earlier turns (`skillPriorTurns`, same
 *           spending rules as a focus); later uses count silently.
 *      Ties go to the credit that arrived first.
 *   6. Next to correction chips the positive chip sits AFTER them in the same
 *      row (the bubble keeps one calm row; the error is read first).
 */
import { nailRevokedBySession, skillPraiseBlocked, type SkillCredit } from '@orborix/shared-types';
import type { VoiceMessage } from '@orborix/voice-client';

export type SkillChipKind = 'completed' | 'nailed' | 'grammar';

export interface SkillChipModel {
  kind: SkillChipKind;
  skillId: string;
  /** The skill's label in the UI language. */
  label: string;
  /** The learner's words that earned it. */
  evidence: string;
  /** Counted reps (capped at the target) and the target. */
  reps: number;
  repsToComplete: number;
  /**
   * `nailed`: what the chip names — the learner's own words when they are a
   * short phrase (the D9 «wait for»), else the skill label. Other kinds: `label`.
   */
  headline: string;
  /** `headline` is the learner's own words (shown in quotes), not a label. */
  quoted: boolean;
  /** `nailed`: the radar focus's short label (context on tap), or null. */
  focusLabel: string | null;
}

/** A phrase short enough to quote on a chip («wait for»). */
export const NAILED_QUOTE_MAX_WORDS = 4;
export const NAILED_QUOTE_MAX_CHARS = 30;

export function isShortPhrase(text: string): boolean {
  const t = text.trim();
  return (
    t.length > 0 &&
    t.length <= NAILED_QUOTE_MAX_CHARS &&
    t.split(/\s+/).length <= NAILED_QUOTE_MAX_WORDS
  );
}

const PRIORITY: Record<SkillChipKind, number> = { completed: 0, nailed: 1, grammar: 2 };

/**
 * Whether a credit's focus — or its skill's generic chip — can still show on
 * its bubble:
 *   - `free`: no earlier nail of the focus (chip of the skill) in this session showed;
 *   - `spent`: one did (or may have, unseen: see below);
 *   - `pending`: an earlier one is waiting for its verdict — it may still show.
 */
type SpendState = 'free' | 'spent' | 'pending';

/** What a bubble shows: its chip and the focus that chip came from, nothing, or not yet decidable. */
type BubbleDecision = { chip: SkillChipModel; focusId: string | null } | null | 'pending';

function chipFor(
  credit: SkillCredit,
  nail: SpendState,
  skill: SpendState,
  locale: 'es' | 'en',
): SkillChipModel | null {
  if (credit.display === 'silent') return null;
  const label = credit.label[locale];
  const base = {
    skillId: credit.skillId,
    label,
    evidence: credit.evidence,
    reps: Math.min(credit.reps, credit.repsToComplete),
    repsToComplete: credit.repsToComplete,
    headline: label,
    quoted: false,
    focusLabel: null,
  };
  if (credit.display === 'chips' && credit.completedNow && credit.celebrate && credit.counted) {
    return { ...base, kind: 'completed' };
  }
  if (credit.nailedFocus && nail === 'free') {
    const quoted = isShortPhrase(credit.evidence);
    return {
      ...base,
      kind: 'nailed',
      headline: quoted ? credit.evidence.trim() : label,
      quoted,
      focusLabel: credit.nailedFocus.label,
    };
  }
  // A skill complete before this credit keeps counting reps, but "3/3" again
  // (up to twice per session, every session) is no news.
  const completedBefore = credit.completed && !credit.completedNow;
  if (
    credit.display === 'chips' &&
    skill === 'free' &&
    credit.area === 'grammar' &&
    credit.celebrate &&
    credit.counted &&
    !completedBefore
  ) {
    return { ...base, kind: 'grammar' };
  }
  return null;
}

/** The credit could show a generic (`grammar`) chip if its skill were still free this session. */
function wantsGenericChip(credit: SkillCredit): boolean {
  return (
    credit.display === 'chips' &&
    credit.area === 'grammar' &&
    credit.celebrate &&
    credit.counted &&
    !(credit.completed && !credit.completedNow)
  );
}

/**
 * Chip per message index (the index in `messages`). Bubbles without a chip are
 * absent from the map.
 *
 * "Once per focus per session" follows the SERVER's record, not the thread's
 * order: each nail carries the turns whose credits nailed the same focus
 * before it was recorded (`nailedFocus.priorTurns`, this session, any
 * connection). The focus is spent by the first of those whose bubble showed a
 * chip from it, so
 *   - a late credit of an earlier bubble never takes the chip from a later
 *     bubble that already shows it (the later one was recorded first);
 *   - after a reload the earlier nails' bubbles are history (no credits, no
 *     turn index): an earlier nail the thread cannot see counts as shown —
 *     a chip may be withheld, never repeated;
 *   - an earlier nail withheld by its own turn's verdict (a correction, a turn
 *     never analysed) does not spend the focus;
 *   - while an earlier nail still waits for its verdict, the later bubble
 *     shows nothing yet (unless its completion wins anyway).
 */
export function selectSkillChips(
  messages: ReadonlyArray<VoiceMessage>,
  locale: 'es' | 'en' = 'es',
): ReadonlyMap<number, SkillChipModel> {
  /** Live learner bubbles by the server's turn index: where an earlier nail is looked up. */
  const byTurn = new Map<number, number>();
  messages.forEach((m, i) => {
    if (m.who === 'user' && !m.historical && m.turnIndex !== undefined) byTurn.set(m.turnIndex, i);
  });
  const decided = new Map<number, BubbleDecision>();
  const deciding = new Set<number>();
  /** Every correction the thread holds, any bubble: an error of a skill takes its nail away. */
  const threadCorrections = messages.flatMap((m) => (m.who === 'user' ? (m.corrections ?? []) : []));

  /**
   * Whether an earlier bubble of `priorTurns` showed a chip `from` this
   * focus / skill. Not in the live thread (history after a reload), or a
   * cycle the server's order rules out: assume it showed.
   */
  const spendState = (
    priorTurns: readonly number[],
    from: (prior: { chip: SkillChipModel; focusId: string | null }) => boolean,
  ): SpendState => {
    let pending = false;
    for (const turn of priorTurns) {
      const j = byTurn.get(turn);
      if (j === undefined || deciding.has(j)) return 'spent';
      const prior = decide(j);
      if (prior === 'pending') pending = true;
      else if (prior !== null && from(prior)) return 'spent';
    }
    return pending ? 'pending' : 'free';
  };
  const nailState = (focus: NonNullable<SkillCredit['nailedFocus']>): SpendState =>
    spendState(focus.priorTurns, (p) => p.focusId === focus.focusId);
  /** One chip per skill per session: the skill's earlier credits (any kind of chip spends it). */
  const skillState = (credit: SkillCredit): SpendState =>
    spendState(credit.skillPriorTurns, (p) => p.chip.skillId === credit.skillId);

  const decide = (i: number): BubbleDecision => {
    const known = decided.get(i);
    if (known !== undefined) return known;
    deciding.add(i);
    const result = decideBubble(i);
    deciding.delete(i);
    decided.set(i, result);
    return result;
  };

  const decideBubble = (i: number): BubbleDecision => {
    const m = messages[i];
    if (!m || m.who !== 'user' || m.historical || !m.skillCredits?.length) return null;
    // Nothing on this bubble can show: no verdict to wait for.
    if (m.skillCredits.every((c) => c.display === 'silent')) return null;
    if (m.correctionsVerdict === undefined) return 'pending';
    if (m.correctionsVerdict.status !== 'ok') return null;
    const corrections = m.corrections ?? [];
    let best: { chip: SkillChipModel; focusId: string | null } | null = null;
    let nailPending = false;
    let skillPending = false;
    for (const credit of m.skillCredits) {
      if (credit.display === 'silent') continue;
      if (skillPraiseBlocked({ skillId: credit.skillId, evidence: credit.evidence }, corrections))
        continue;
      const nail =
        credit.nailedFocus && !nailRevokedBySession(credit.skillId, threadCorrections)
          ? nailState(credit.nailedFocus)
          : 'spent';
      if (nail === 'pending') nailPending = true;
      const skill = wantsGenericChip(credit) ? skillState(credit) : 'spent';
      if (skill === 'pending') skillPending = true;
      const chip = chipFor(credit, nail, skill, locale);
      if (!chip) continue;
      if (best === null || PRIORITY[chip.kind] < PRIORITY[best.chip.kind]) {
        // A focus is praised once per session: the chip that came from a
        // credit linked to it (a completion of that skill included) spends it.
        best = { chip, focusId: credit.nailedFocus?.focusId ?? null };
      }
    }
    // A nail that may still be this bubble's: decide once it can be told.
    if (nailPending && best?.chip.kind !== 'completed') return 'pending';
    // Same for a skill's first chip, unless something that outranks it already won.
    if (skillPending && (best === null || best.chip.kind === 'grammar')) return 'pending';
    return best;
  };

  const out = new Map<number, SkillChipModel>();
  messages.forEach((_, i) => {
    const d = decide(i);
    if (d !== null && d !== 'pending') out.set(i, d.chip);
  });
  return out;
}

/** How many completion chips the thread holds (drives the sparkles). */
export function completionCount(chips: ReadonlyMap<number, SkillChipModel>): number {
  let n = 0;
  for (const c of chips.values()) if (c.kind === 'completed') n += 1;
  return n;
}
